import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { chatService } from "../services/chatService";
import { useAuth } from "./AuthContext";
import { useChatSocket } from "./ChatSocketContext";

type UnreadEntry = { unreadCount: number; unreadMentions: number };

type ChatContextType = {
  total: number;
  mentions: number;
  byConversation: Record<string, UnreadEntry>;
  /** Lokal als gelesen markieren (optimistisch, ergänzt den Server-Push). */
  markConversationRead: (conversationId: string) => void;
  /** Unread-Stand einer Conversation lokal setzen (z. B. aus unread:update). */
  setConversationUnread: (conversationId: string, entry: UnreadEntry) => void;
  refresh: () => void;
};

const ChatContext = createContext<ChatContextType | undefined>(undefined);

/**
 * Globaler Unread-Zustand des internen Chats — speist das Nav-Badge. Seed über
 * `GET /chat/conversations`, danach live über das `unread:update`-WS-Event (an den
 * eigenen User-Room). Muss INNERHALB von `ChatSocketProvider` liegen.
 */
export const ChatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isPendingApproval } = useAuth();
  const { on, connected } = useChatSocket();
  const [map, setMap] = useState<Record<string, UnreadEntry>>({});
  const loadedRef = useRef(false);

  const refresh = useCallback(() => {
    if (!isAuthenticated || isPendingApproval) return;
    chatService
      .listConversations()
      .then((convs) => {
        const next: Record<string, UnreadEntry> = {};
        for (const c of convs) {
          next[c.id] = {
            unreadCount: c.unreadCount ?? 0,
            unreadMentions: c.unreadMentions ?? 0,
          };
        }
        setMap(next);
      })
      .catch(() => undefined);
  }, [isAuthenticated, isPendingApproval]);

  useEffect(() => {
    if (!isAuthenticated || isPendingApproval) {
      setMap({});
      loadedRef.current = false;
      return;
    }
    if (!loadedRef.current) {
      loadedRef.current = true;
      refresh();
    }
  }, [isAuthenticated, isPendingApproval, refresh]);

  // Beim (Re)connect frisch laden — fängt verpasste Updates während Offline ab.
  // Der ERSTE Connect direkt nach dem Initial-Load wird übersprungen (sonst 2×
  // GET /chat/conversations beim Start); nur echte Re-Connects laden neu.
  const skipFirstConnectRef = useRef(true);
  useEffect(() => {
    if (!connected) return;
    if (skipFirstConnectRef.current) {
      skipFirstConnectRef.current = false;
      return;
    }
    refresh();
  }, [connected, refresh]);

  useEffect(() => {
    const off = on(
      "unread:update",
      (payload: { conversationId: string; unreadCount: number; unreadMentions: number }) => {
        setMap((prev) => ({
          ...prev,
          [payload.conversationId]: {
            unreadCount: payload.unreadCount ?? 0,
            unreadMentions: payload.unreadMentions ?? 0,
          },
        }));
      },
    );
    return off;
  }, [on]);

  const markConversationRead = useCallback((conversationId: string) => {
    setMap((prev) => ({ ...prev, [conversationId]: { unreadCount: 0, unreadMentions: 0 } }));
  }, []);

  const setConversationUnread = useCallback((conversationId: string, entry: UnreadEntry) => {
    setMap((prev) => ({ ...prev, [conversationId]: entry }));
  }, []);

  const { total, mentions } = useMemo(() => {
    let t = 0;
    let m = 0;
    for (const e of Object.values(map)) {
      t += e.unreadCount || 0;
      m += e.unreadMentions || 0;
    }
    return { total: t, mentions: m };
  }, [map]);

  const value = useMemo<ChatContextType>(
    () => ({
      total,
      mentions,
      byConversation: map,
      markConversationRead,
      setConversationUnread,
      refresh,
    }),
    [total, mentions, map, markConversationRead, setConversationUnread, refresh],
  );

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

export const useChat = (): ChatContextType => {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error("useChat must be used within a ChatProvider");
  return ctx;
};

/** Optionale Variante für Komponenten außerhalb des Providers (z. B. Nav). */
export const useChatOptional = (): ChatContextType | null => useContext(ChatContext) ?? null;
